import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppMutation from 'hooks/useAppMutation';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import { DbsWhiteLabelSettings } from 'models/AgencyDbs';
import API from 'services/API';
import { refreshAppConfig } from 'store/slices/config';
import { DBS_WHITE_LABEL_SETTINGS_BASE_QUERY_KEY } from './useFetchDbsWhiteLabelSettings';

export const AGENCY_FAVICON_URL = '/api/internal/photos/agency-favicon';

interface UploadAgencyFaviconResponseTO {
  fileName: string;
  uid: string;
}

const useUploadAgencyFaviconMutation = () => {
  const { agencyUid } = useAppUser();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { notifyError, notifySuccess } = useNotify();
  const { t } = useTranslation();

  return useAppMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post<UploadAgencyFaviconResponseTO>(
        AGENCY_FAVICON_URL,
        formData,
      );

      return response.data;
    },
    onSuccess: ({ fileName }) => {
      // the response carries the stored URL, so the tab shows it without re-reading the site
      queryClient.setQueryData<DbsWhiteLabelSettings>(
        [DBS_WHITE_LABEL_SETTINGS_BASE_QUERY_KEY, agencyUid],
        (current) => ({
          enabled: false,
          domain: null,
          ...current,
          faviconUrl: fileName,
        }),
      );
      // the PMP's own tab icon comes from the app config, refreshed like the legacy flow does
      dispatch(refreshAppConfig());
      notifySuccess(
        t('pageDirectBookingEngine.whiteLabelSettings.faviconSaved'),
      );
    },
    onError: () => {
      notifyError(
        t('pageDirectBookingEngine.whiteLabelSettings.faviconUploadError'),
      );
    },
  });
};

export default useUploadAgencyFaviconMutation;
